import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { syncAllStaticCoursesToDatabase } from '@/services/courseDataSyncService';
import { Loader2, CheckCircle, XCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import CourseSyncDashboard from './CourseSyncDashboard';

export default function CourseSyncUtility() {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const { toast } = useToast();

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);

    try {
      const result = await syncAllStaticCoursesToDatabase();
      setSyncResult(result);

      if (result.errors.length === 0) {
        toast({
          title: 'Sync Complete',
          description: `Successfully synced ${result.totalCourses} courses with ${result.totalModules} modules`,
        });
      } else {
        toast({
          title: 'Sync Completed with Errors',
          description: `${result.totalCourses} courses synced, but ${result.errors.length} errors occurred`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  if (showDashboard) {
    return (
      <div>
        <div className="container mx-auto p-6">
          <Button variant="outline" onClick={() => setShowDashboard(false)} className="mb-4">
            ← Back to Sync Utility
          </Button>
        </div>
        <CourseSyncDashboard />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Course Database Sync Utility</h1>
          <p className="text-muted-foreground">
            Sync all static course data to the database
          </p>
        </div>
        <Button variant="outline" onClick={() => setShowDashboard(true)}>
          <ExternalLink className="h-4 w-4 mr-2" />
          View Sync Dashboard
        </Button>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold mb-2">Phase 1: Static Content Sync</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Enhanced sync covering ALL 60+ categories to populate 222+ courses:
            </p>
            <div className="bg-muted/50 p-4 rounded-lg mb-4">
              <ul className="text-xs grid grid-cols-4 gap-2">
                <li>✓ Python</li>
                <li>✓ Java</li>
                <li>✓ JavaScript</li>
                <li>✓ C#/.NET</li>
                <li>✓ Rust</li>
                <li>✓ AWS</li>
                <li>✓ Azure</li>
                <li>✓ GCP</li>
                <li>✓ SQL</li>
                <li>✓ ETL</li>
                <li>✓ PowerBI</li>
                <li>✓ Tableau</li>
                <li>✓ Data Science</li>
                <li>✓ ML/AI</li>
                <li>✓ DevOps</li>
                <li>✓ Kubernetes</li>
                <li>✓ React</li>
                <li>✓ Frontend</li>
                <li>✓ Backend</li>
                <li>✓ APIs</li>
                <li>✓ Security</li>
                <li>✓ AppSec</li>
                <li>✓ Testing</li>
                <li>✓ Selenium</li>
                <li>✓ Mobile</li>
                <li>✓ Cross-Platform</li>
                <li>✓ Blockchain</li>
                <li>✓ Web3</li>
                <li>✓ Graphics</li>
                <li>✓ Game Dev</li>
                <li>✓ UI/UX</li>
                <li>✓ RPA</li>
                <li>✓ Enterprise</li>
                <li>✓ Professional Dev</li>
                <li>✓ Infrastructure</li>
                <li>✓ Edge Computing</li>
                <li>✓ Green Tech</li>
                <li>✓ No-Code</li>
                <li>...and more</li>
              </ul>
            </div>
          </div>

          <Button 
            onClick={handleSync} 
            disabled={syncing}
            size="lg"
            className="w-full"
          >
            {syncing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing Courses...
              </>
            ) : (
              'Start Database Sync'
            )}
          </Button>
        </div>
      </Card>

      {syncResult && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Sync Results</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Total Courses</span>
                </div>
                <p className="text-2xl font-bold">{syncResult.totalCourses}</p>
              </div>

              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Total Modules</span>
                </div>
                <p className="text-2xl font-bold">{syncResult.totalModules}</p>
              </div>
            </div>

            {Object.entries(syncResult.categoryResults).map(([category, result]: [string, any]) => (
              <div key={category} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold capitalize">{category}</h3>
                  {result.success ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  <p>Courses synced: {result.coursesSynced}</p>
                  <p>Modules synced: {result.modulesSynced}</p>
                  {result.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-red-500 font-semibold">Errors:</p>
                      <ul className="list-disc list-inside">
                        {result.errors.map((error: string, idx: number) => (
                          <li key={idx} className="text-red-500 text-xs">{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {syncResult.errors.length > 0 && (
              <div className="border border-red-500/20 bg-red-500/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <h3 className="font-semibold text-red-500">Global Errors</h3>
                </div>
                <ul className="text-sm space-y-1">
                  {syncResult.errors.map((error: string, idx: number) => (
                    <li key={idx} className="text-red-500">{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
